import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerInput, formatVndIntegerValue, parseVndIntegerInput, to_vietnamese } from "utils";
import { GoodsReceivedNoteService } from "services/goods-received-note";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  itemName: "",
  itemCode: "",
  unit: "",
  quantityByDocument: "",
  quantityReceived: "",
  unitPrice: "",
  amount: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const parseMoney = (value) => {
  const amount = parseVndIntegerInput(value);
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return Math.round(amount);
};

const formatQuantityInput = (value) => {
  return String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
};

const moneyToWords = (value) => {
  const numeric = parseMoney(value);
  if (!numeric) return "";
  const words = to_vietnamese(numeric).trim();
  if (!words) return "";
  const normalized = words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
  if (normalized.endsWith("đồng")) return normalized;
  return `${normalized} đồng`;
};

export default function GoodsReceivedNoteDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });
  const [referenceDateParts, setReferenceDateParts] = useState({ day: "", month: "", year: "" });
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    debitCode: "",
    creditCode: "",
    delivererName: "",
    referenceDocumentType: "",
    referenceDocumentNumber: "",
    referenceDocumentIssuer: "",
    warehouseName: "",
    warehouseLocation: "",
    attachedOriginalDocuments: "",
    totalAmountInWords: "",
  });
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await GoodsReceivedNoteService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          debitCode: data.debitCode || "",
          creditCode: data.creditCode || "",
          delivererName: data.delivererName || "",
          referenceDocumentType: data.referenceDocumentType || "",
          referenceDocumentNumber: data.referenceDocumentNumber || "",
          referenceDocumentIssuer: data.referenceDocumentIssuer || "",
          warehouseName: data.warehouseName || "",
          warehouseLocation: data.warehouseLocation || "",
          attachedOriginalDocuments: data.attachedOriginalDocuments || "",
          totalAmountInWords: data.totalAmountInWords || "",
        });

        setDocumentDateParts(splitDate(data.documentDate || ""));
        setReferenceDateParts(splitDate(data.referenceDocumentDate || ""));

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          itemCode: row.itemCode || "",
          unit: row.unit || "",
          quantityByDocument: String(row.quantityByDocument || ""),
          quantityReceived: String(row.quantityReceived || ""),
          unitPrice: formatVndIntegerInput(row.unitPrice || 0),
          amount: formatVndIntegerInput(row.amount || 0),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu phiếu nhập kho");
      }
    })();
  }, [id]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        quantityByDocument: acc.quantityByDocument + parseQty(row.quantityByDocument),
        quantityReceived: acc.quantityReceived + parseQty(row.quantityReceived),
        amount: acc.amount + parseMoney(row.amount),
      }),
      { quantityByDocument: 0, quantityReceived: 0, amount: 0 }
    );
  }, [rows]);

  useEffect(() => {
    setHeader((prev) => ({
      ...prev,
      totalAmountInWords: moneyToWords(totals.amount),
    }));
  }, [totals.amount]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (setter, field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setter((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (setter, field) => {
    if (field === "year") return;
    setter((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const recalculateAmount = (row) => {
    const quantityReceived = parseQty(row.quantityReceived);
    const unitPrice = parseMoney(row.unitPrice);
    const amount = Math.round(quantityReceived * unitPrice);
    return { ...row, amount: formatVndIntegerInput(amount) };
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const current = { ...next[index] };

      if (field === "unitPrice") {
        current.unitPrice = formatVndIntegerInput(value);
        next[index] = recalculateAmount(current);
      } else if (field === "quantityByDocument" || field === "quantityReceived") {
        current[field] = formatQuantityInput(value);
        next[index] = field === "quantityReceived" ? recalculateAmount(current) : current;
      } else {
        current[field] = value;
        next[index] = current;
      }

      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const debitCode = String(header.debitCode || "").trim();
    const creditCode = String(header.creditCode || "").trim();
    const delivererName = String(header.delivererName || "").trim();
    const referenceDocumentType = String(header.referenceDocumentType || "").trim();
    const referenceDocumentNumber = String(header.referenceDocumentNumber || "").trim();
    const referenceDocumentIssuer = String(header.referenceDocumentIssuer || "").trim();
    const warehouseName = String(header.warehouseName || "").trim();
    const warehouseLocation = String(header.warehouseLocation || "").trim();
    const attachedOriginalDocuments = String(header.attachedOriginalDocuments || "").trim();
    const totalAmountInWords = String(header.totalAmountInWords || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số phiếu không được để trống");
    if (!delivererName) throw new Error("Họ và tên người giao không được để trống");
    if (!warehouseName) throw new Error("Nhập tại kho không được để trống");

    const normalizedRows = rows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        itemCode: String(row.itemCode || "").trim(),
        unit: String(row.unit || "").trim(),
        quantityByDocument: parseQty(row.quantityByDocument),
        quantityReceived: parseQty(row.quantityReceived),
        unitPrice: parseMoney(row.unitPrice),
        amount: parseMoney(row.amount),
      }))
      .filter((row) => {
        const hasAmount = row.quantityByDocument > 0 || row.quantityReceived > 0 || row.unitPrice > 0 || row.amount > 0;
        return row.itemName || row.itemCode || row.unit || hasAmount;
      });

    if (!normalizedRows.length) {
      throw new Error("Phiếu nhập kho phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.itemName) throw new Error(`Dòng ${index + 1}: Tên vật tư/hàng hóa không được để trống`);
    });

    const documentDate = validateDate(documentDateParts, "Ngày phiếu");
    const hasReferenceDate = referenceDateParts.day || referenceDateParts.month || referenceDateParts.year;
    const referenceDocumentDate = hasReferenceDate ? validateDate(referenceDateParts, "Ngày chứng từ") : "";

    return {
      unit,
      department,
      documentDate,
      documentNumber,
      debitCode,
      creditCode,
      delivererName,
      referenceDocumentType,
      referenceDocumentNumber,
      referenceDocumentDate,
      referenceDocumentIssuer,
      warehouseName,
      warehouseLocation,
      attachedOriginalDocuments,
      totalAmountInWords,
      rows: normalizedRows,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await GoodsReceivedNoteService.create(payload);
        toast.success("Tạo phiếu nhập kho thành công");
      } else {
        await GoodsReceivedNoteService.update(id, payload);
        toast.success("Cập nhật phiếu nhập kho thành công");
      }
      navigation("/templates/goods-received-note");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        documentDate: composeDate(documentDateParts),
        referenceDocumentDate: composeDate(referenceDateParts),
      },
      rows: rows.map((row, index) => ({
        ...row,
        serialNumber: index + 1,
      })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 1000 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 210 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 210 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 700 }}>Mẫu số: 01 - VT</div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC
                      <br />
                      ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "10px 0 4px", textTransform: "uppercase" }}>PHIẾU NHẬP KHO</h2>

                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 36, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.day}
                    onChange={(event) => setDateField(setDocumentDateParts, "day", event.target.value)}
                    onBlur={() => padDateField(setDocumentDateParts, "day")}
                  />
                  tháng
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 36, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.month}
                    onChange={(event) => setDateField(setDocumentDateParts, "month", event.target.value)}
                    onBlur={() => padDateField(setDocumentDateParts, "month")}
                  />
                  năm
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 60, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.year}
                    onChange={(event) => setDateField(setDocumentDateParts, "year", event.target.value)}
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 20, marginBottom: 8 }}>
                  <div>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 90 }}
                      value={header.documentNumber}
                      onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Nợ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 90 }}
                      value={header.debitCode}
                      onChange={(event) => setHeaderField("debitCode", event.target.value)}
                    />
                  </div>
                  <div>
                    Có:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 90 }}
                      value={header.creditCode}
                      onChange={(event) => setHeaderField("creditCode", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 6 }}>
                  - Họ và tên người giao:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 280 }}
                    value={header.delivererName}
                    onChange={(event) => setHeaderField("delivererName", event.target.value)}
                  />
                </div>

                <div style={{ marginBottom: 6 }}>
                  - Theo
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 130, margin: "0 4px" }}
                    value={header.referenceDocumentType}
                    onChange={(event) => setHeaderField("referenceDocumentType", event.target.value)}
                  />
                  số
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 90, margin: "0 4px" }}
                    value={header.referenceDocumentNumber}
                    onChange={(event) => setHeaderField("referenceDocumentNumber", event.target.value)}
                  />
                  ngày
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={referenceDateParts.day}
                    onChange={(event) => setDateField(setReferenceDateParts, "day", event.target.value)}
                    onBlur={() => padDateField(setReferenceDateParts, "day")}
                  />
                  tháng
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={referenceDateParts.month}
                    onChange={(event) => setDateField(setReferenceDateParts, "month", event.target.value)}
                    onBlur={() => padDateField(setReferenceDateParts, "month")}
                  />
                  năm
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 60, textAlign: "center", margin: "0 4px" }}
                    value={referenceDateParts.year}
                    onChange={(event) => setDateField(setReferenceDateParts, "year", event.target.value)}
                  />
                  của
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 180, margin: "0 4px" }}
                    value={header.referenceDocumentIssuer}
                    onChange={(event) => setHeaderField("referenceDocumentIssuer", event.target.value)}
                  />
                </div>

                <div style={{ marginBottom: 10 }}>
                  Nhập tại kho:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 240 }}
                    value={header.warehouseName}
                    onChange={(event) => setHeaderField("warehouseName", event.target.value)}
                  />
                  địa điểm
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 220, marginLeft: 6 }}
                    value={header.warehouseLocation}
                    onChange={(event) => setHeaderField("warehouseLocation", event.target.value)}
                  />
                </div>

                <div style={{ width: "100%", overflowX: "auto" }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th }} rowSpan={2}>
                          STT
                        </th>
                        <th style={{ ...s.th, minWidth: 260 }} rowSpan={2}>
                          Tên, nhãn hiệu, quy cách, phẩm chất vật tư, dụng cụ sản phẩm, hàng hóa
                        </th>
                        <th style={{ ...s.th, minWidth: 70 }} rowSpan={2}>
                          Mã số
                        </th>
                        <th style={{ ...s.th, minWidth: 90 }} rowSpan={2}>
                          Đơn vị tính
                        </th>
                        <th style={{ ...s.th, minWidth: 180 }} colSpan={2}>
                          Số lượng
                        </th>
                        <th style={{ ...s.th, minWidth: 100 }} rowSpan={2}>
                          Đơn giá
                        </th>
                        <th style={{ ...s.th, minWidth: 120 }} rowSpan={2}>
                          Thành tiền
                        </th>
                        {!containView && (
                          <th style={{ ...s.th, width: 40 }} rowSpan={3}>
                            #
                          </th>
                        )}
                      </tr>
                      <tr>
                        <th style={{ ...s.th, minWidth: 90 }}>Theo chứng từ</th>
                        <th style={{ ...s.th, minWidth: 90 }}>Thực nhập</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={index}>
                          <td style={s.td}>
                            <input disabled style={s.inputCenter} value={index + 1} />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.itemName}
                              onChange={(event) => setRowField(index, "itemName", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.itemCode}
                              onChange={(event) => setRowField(index, "itemCode", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.unit}
                              onChange={(event) => setRowField(index, "unit", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityByDocument}
                              onChange={(event) => setRowField(index, "quantityByDocument", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityReceived}
                              onChange={(event) => setRowField(index, "quantityReceived", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.unitPrice}
                              onChange={(event) => setRowField(index, "unitPrice", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input disabled style={s.inputRight} value={row.amount} />
                          </td>
                          {!containView && (
                            <td style={{ ...s.td, width: 28, textAlign: "center" }}>
                              <button
                                type="button"
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#e53935",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                }}
                                onClick={() => removeRow(index)}
                              >
                                X
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...s.th }} colSpan={4}>
                          Cộng
                        </td>
                        <td style={{ ...s.th, textAlign: "right" }}>{totals.quantityByDocument.toFixed(3).replace(/\.000$/, "")}</td>
                        <td style={{ ...s.th, textAlign: "right" }}>{totals.quantityReceived.toFixed(3).replace(/\.000$/, "")}</td>
                        <td style={{ ...s.th }}>x</td>
                        <td style={{ ...s.th, textAlign: "right" }}>{formatVndIntegerValue(totals.amount)}</td>
                        {!containView && <td style={{ ...s.th }}></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 10 }}>
                  - Tổng số tiền (viết bằng chữ):{" "}
                  <input disabled style={{ ...s.inputDotted, width: 420 }} value={header.totalAmountInWords} />
                </div>
                <div style={{ marginTop: 6 }}>
                  - Số chứng từ gốc kèm theo:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 200 }}
                    value={header.attachedOriginalDocuments}
                    onChange={(event) => setHeaderField("attachedOriginalDocuments", event.target.value)}
                  />
                </div>
              </SoftBox>

                <SoftBox display="flex" justifyContent="space-between" m={2}>
                  <SoftButton onClick={() => navigation("/templates/goods-received-note")}>Trở về</SoftButton>
                  <SoftBox display="flex" gap={1}>
                  {!containView && (
                    <SoftButton variant="outlined" color="dark" onClick={addRow}>
                      THÊM DÒNG
                    </SoftButton>
                  )}
                  {containView && (
                    <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                      In Bảng
                    </SoftButton>
                  )}
                  {!containView && (
                    <SoftButton variant="gradient" color="dark" disabled={loadingSubmit} onClick={handleSubmit}>
                      {id ? "Lưu" : "Tạo"}
                    </SoftButton>
                  )}
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
